/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { getConfluenceContentType } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';

import { convertContentType } from '../base-page-selector';
import type { ConfluenceEntityResult } from '../base-page-selector/types';
import { DebouncedSelector } from '../debounced-selector';

import type {
	BaseConfluenceSpaceSelectorProps,
	ConfluenceSpaceOption,
	RecommendedSpaceResponse,
} from './types';

export const messages: {
	spaceSelectorLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	suggestedSpacesHeading: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	spaceSelectorLabel: {
		id: 'ai-mate.conversation-assistant-space-selector.space-selector-label',
		defaultMessage: 'Select space',
		description: 'Label for conversation assistant space selector',
	},
	suggestedSpacesHeading: {
		id: 'ai-mate.conversation-assistant-space-selector.suggested-spaces-heading',
		defaultMessage: 'Suggested spaces',
		description: 'Section heading for AI-recommended spaces in the finalize popup',
	},
});

/**
 * Maps a filterContentType value to the corresponding enabledContentTypes field.
 *
 * Note: 'confluence-page' maps to isLivePagesEnabled because this filter is only
 * applied when creating live pages (livedocs). Regular pages are supported by all
 * spaces and should not be filtered — the caller is responsible for passing
 * filterContentType as undefined when creating regular pages.
 */
const getEnabledFlag = (
	enabledContentTypes: {
		isWhiteboardsEnabled?: boolean;
		isLivePagesEnabled?: boolean;
		isDatabasesEnabled?: boolean;
		isSlidesEnabled?: boolean;
	},
	filterContentType: NonNullable<BaseConfluenceSpaceSelectorProps['filterContentType']>,
): boolean | undefined => {
	switch (filterContentType) {
		case 'confluence-whiteboard':
			return enabledContentTypes.isWhiteboardsEnabled;
		case 'confluence-page':
			// 'confluence-page' here refers to live pages (livedocs), not regular pages.
			// Regular pages are supported by all spaces and are not filtered.
			return enabledContentTypes.isLivePagesEnabled;
		case 'confluence-database':
			return enabledContentTypes.isDatabasesEnabled;
		case 'confluence-slide':
			return enabledContentTypes.isSlidesEnabled;
		default:
			return undefined;
	}
};

type SpaceFilterableContentType = Extract<
	ReturnType<typeof getConfluenceContentType>,
	'confluence-page' | 'confluence-whiteboard' | 'confluence-database' | 'confluence-slide'
>;

const isSpaceFilterableContentType = (
	contentType: ReturnType<typeof getConfluenceContentType>,
): contentType is SpaceFilterableContentType =>
	contentType === 'confluence-page' ||
	contentType === 'confluence-whiteboard' ||
	contentType === 'confluence-database' ||
	contentType === 'confluence-slide';

export const getSpaceFilterContentType = ({
	contentType,
	selectedContentType,
}: {
	contentType: ReturnType<typeof getConfluenceContentType>;
	selectedContentType: 'livedoc' | 'page' | null;
}): SpaceFilterableContentType | undefined => {
	if (!isSpaceFilterableContentType(contentType)) {
		return undefined;
	}

	// Regular pages are supported by all spaces.
	if (contentType === 'confluence-page' && selectedContentType === 'page') {
		return undefined;
	}

	return contentType;
};

/**
 * Checks if a space supports the specified content type based on its type settings.
 * When filterContentType is set, only spaces that have the corresponding content type
 * enabled in their enabledContentTypes settings will be included.
 * If no settings data is available, the space will not be included.
 */
export const supportsContentType = (
	space: {
		typeSettings?: {
			enabledContentTypes?: {
				isWhiteboardsEnabled?: boolean;
				isBlogsEnabled?: boolean;
				isDatabasesEnabled?: boolean;
				isLivePagesEnabled?: boolean;
				isEmbedsEnabled?: boolean;
				isFoldersEnabled?: boolean;
				isSlidesEnabled?: boolean;
			};
		};
	},
	filterContentType?: BaseConfluenceSpaceSelectorProps['filterContentType'],
): boolean => {
	if (!filterContentType) {
		return true;
	}

	const enabledContentTypes = space?.typeSettings?.enabledContentTypes;
	if (!enabledContentTypes) {
		return false;
	}

	const isEnabled = getEnabledFlag(enabledContentTypes, filterContentType);
	return isEnabled !== false;
};

/**
 * Safely checks the gate controlling whether spaces that disallow first publish
 * (approvals) are filtered out of the picker. Uses the same Approvals Beta killswitch
 * as the monolith first-publish gate so the picker behavior stays in lockstep with the
 * backend. Returns false if the Statsig SDK throws so the component degrades gracefully
 * to today's behavior (no filtering).
 */
const isFirstPublishFilterEnabled = (): boolean => {
	try {
		// TODO CCIMPORTER-3221: register `confluence_approvals_eap` in
		// `@atlaskit/tmp-editor-statsig` (`experiments-config.ts`) so the
		// experiment id is known to TypeScript and this cast can be
		// removed. Matches the same cast in store-error/index.tsx and
		// confluence-object-finalize-popup/index.tsx.
		if (
			expValEqualsNoExposure(
				'confluence_approvals_eap' as Parameters<typeof expValEqualsNoExposure>[0],
				'isEnabled',
				true,
			)
		) {
			return true;
		}
		return false;
	} catch {
		return false;
	}
};

/**
 * Filters out spaces that disallow first publish, using a batched approvals lookup.
 *
 * No-op (returns the input unchanged) when the feature gate is off, when no
 * fetcher is provided, or when the input is empty. Degrades gracefully: any space
 * whose config is missing from the resolved map is treated as allowed (kept), so a
 * failed/partial lookup never incorrectly hides a space.
 */
const filterOutFirstPublishDisallowedSpaces = async <T,>(
	spaces: T[],
	getSpaceKey: (space: T) => string | undefined,
	fetchSpaceApprovalsConfigs?: (
		spaceKeys: string[],
	) => Promise<Record<string, { disallowFirstPublish: boolean }>>,
): Promise<T[]> => {
	if (!isFirstPublishFilterEnabled() || !fetchSpaceApprovalsConfigs || spaces.length === 0) {
		return spaces;
	}

	const keys = spaces
		.map(getSpaceKey)
		.filter((key): key is string => typeof key === 'string' && key.length > 0);
	if (keys.length === 0) {
		return spaces;
	}

	const approvalsConfigs = await fetchSpaceApprovalsConfigs(keys);
	return spaces.filter((space) => {
		const key = getSpaceKey(space);
		if (!key) {
			return true;
		}
		return approvalsConfigs[key]?.disallowFirstPublish !== true;
	});
};

export const BaseConfluenceSpaceSelectorInner = ({
	userId,
	recommendedSpaces,
	listEntities,
	fetchRecommendedSpaces,
	fetchSpaceApprovalsConfigs,
	filterContentType,
	onFallbackError,
	onFallbackSuccess,
	...props
}: BaseConfluenceSpaceSelectorProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const mapSearchResultsToOptions = useCallback((spaces: ConfluenceEntityResult[]) => {
		return spaces.map((space) => ({
			label: space.name,
			value: space.id,
			key: space.key,
			...(space.homepage && {
				homepage: {
					label: space.homepage.name,
					value: space.homepage.id,
					type: convertContentType(space.homepage.type),
				},
			}),
			iconPath: space.iconPath,
			type: space.type,
			typeSettings: space.typeSettings,
		}));
	}, []);

	const shouldUseSmartRecommendedSpaces = recommendedSpaces !== undefined;

	const [hasSearchText, setHasSearchText] = useState(false);

	// Memoised cache of per-space approvals lookups so that successive debounced
	// search invocations (each calling `fetchSpaceApprovalsConfigs` for the new
	// page of results) do not re-fetch the same space keys we already resolved
	// in a prior call. The cache is keyed by space key and only stores the boolean
	// `disallowFirstPublish`; this is intentionally per-render-instance and not a
	// global cache, so it lives and dies with the picker mount.
	const approvalsCacheRef = useRef<Map<string, boolean>>(new Map());

	// Reset the cache when the underlying fetcher reference changes (e.g. on a
	// different cloudId / siteId), to avoid serving stale results across tenants.
	useEffect(() => {
		approvalsCacheRef.current = new Map();
	}, [fetchSpaceApprovalsConfigs]);

	const cachedFetchSpaceApprovalsConfigs = useMemo<typeof fetchSpaceApprovalsConfigs>(() => {
		if (!fetchSpaceApprovalsConfigs) {
			return undefined;
		}
		return async (spaceKeys: string[]) => {
			const result: Record<string, { disallowFirstPublish: boolean }> = {};
			const missing: string[] = [];
			for (const key of spaceKeys) {
				const cached = approvalsCacheRef.current.get(key);
				if (cached !== undefined) {
					result[key] = { disallowFirstPublish: cached };
				} else {
					missing.push(key);
				}
			}
			if (missing.length === 0) {
				return result;
			}
			const fetched = await fetchSpaceApprovalsConfigs(missing);
			for (const key of missing) {
				const disallowed = fetched[key]?.disallowFirstPublish === true;
				approvalsCacheRef.current.set(key, disallowed);
				result[key] = { disallowFirstPublish: disallowed };
			}
			return result;
		};
	}, [fetchSpaceApprovalsConfigs]);

	// Space keys (from the pre-fetched suggested spaces) that disallow first publish
	// and should be hidden from the synchronous `options` render. Resolved via the
	// batched approvals lookup. Empty by default so the picker behaves as today until
	// (and unless) the gate is on and the lookup resolves.
	const [firstPublishDisallowedKeys, setFirstPublishDisallowedKeys] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	useEffect(() => {
		if (!isFirstPublishFilterEnabled() || !fetchSpaceApprovalsConfigs || !recommendedSpaces) {
			// Do not reset state to a fresh `new Set()` here — the initial
			// state is already `new Set()`, and because `Object.is(new Set(),
			// new Set())` is `false`, an unconditional setter call would
			// schedule a re-render on every dep change even though the
			// effective state did not change. Only reset when we know there
			// is something to clear.
			setFirstPublishDisallowedKeys((prev) => (prev.size === 0 ? prev : new Set()));
			return;
		}

		const keys = recommendedSpaces
			.map((space) => space.spaceDetails.key)
			.filter((key): key is string => typeof key === 'string' && key.length > 0);
		if (keys.length === 0) {
			// Same rationale as the early-return above: only reset when we know
			// the previous state actually held something.
			setFirstPublishDisallowedKeys((prev) => (prev.size === 0 ? prev : new Set()));
			return;
		}

		let cancelled = false;
		// Use the cached wrapper here too so the effect's batch is co-cached
		// with the loadOptions paths — if a user types and then closes/reopens
		// the picker we don't re-query the same space keys.
		const fetcher = cachedFetchSpaceApprovalsConfigs ?? fetchSpaceApprovalsConfigs;
		fetcher(keys)
			.then((configs) => {
				if (cancelled) {
					return;
				}
				const disallowed = new Set(
					Object.entries(configs)
						.filter(([, config]) => config?.disallowFirstPublish === true)
						.map(([key]) => key),
				);
				setFirstPublishDisallowedKeys(disallowed);
			})
			.catch(() => {
				// Degrade gracefully: on error, hide nothing. Same idempotent
				// reset as the early-return paths so we don't churn renders.
				if (!cancelled) {
					setFirstPublishDisallowedKeys((prev) => (prev.size === 0 ? prev : new Set()));
				}
			});

		return () => {
			cancelled = true;
		};
	}, [recommendedSpaces, fetchSpaceApprovalsConfigs, cachedFetchSpaceApprovalsConfigs]);

	/**
	 * Runs the listEntities fallback when recommended spaces are empty.
	 * Shared between the fetchRecommendedSpaces path and the pre-fetched
	 * recommendedSpaces prop path.
	 */
	const runFallback = useCallback(async (): Promise<ConfluenceSpaceOption[]> => {
		try {
			const fallbackSpaces = await listEntities('', ['space']);
			const contentTypeFiltered = fallbackSpaces.filter((space) =>
				supportsContentType(space, filterContentType),
			);
			const filteredFallback = await filterOutFirstPublishDisallowedSpaces(
				contentTypeFiltered,
				(space) => space.key,
				cachedFetchSpaceApprovalsConfigs,
			);
			const options = mapSearchResultsToOptions(filteredFallback);
			onFallbackSuccess?.({ fallbackResultCount: options.length });
			return options;
		} catch (error) {
			onFallbackError?.(error);
			return [];
		}
	}, [
		listEntities,
		filterContentType,
		cachedFetchSpaceApprovalsConfigs,
		mapSearchResultsToOptions,
		onFallbackError,
		onFallbackSuccess,
	]);

	const loadOptions = useCallback(
		async (searchText: string) => {
			if (userId && !searchText) {
				// When recommendedSpaces is pre-fetched as a prop (including empty array),
				// the dropdown options are rendered via the `options` prop. However, when
				// the pre-fetched list is empty (or all entries are filtered out), we need
				// to trigger the fallback here so the user isn't stuck with "No options".
				if (shouldUseSmartRecommendedSpaces) {
					const contentTypeFiltered = recommendedSpaces
						? recommendedSpaces.filter((space) =>
								supportsContentType(
									{ typeSettings: space.spaceDetails.spaceTypeSettings },
									filterContentType,
								),
							)
						: [];
					const filtered = await filterOutFirstPublishDisallowedSpaces(
						contentTypeFiltered,
						(space) => space.spaceDetails.key,
						cachedFetchSpaceApprovalsConfigs,
					);

					if (filtered.length === 0) {
						return runFallback();
					}
					return [];
				}

				const response = (await fetchRecommendedSpaces({ userId })) ?? [];
				const contentTypeFiltered = response.filter((space) =>
					supportsContentType({ typeSettings: space.container?.typeSettings }, filterContentType),
				);
				const filteredSpaces = await filterOutFirstPublishDisallowedSpaces(
					contentTypeFiltered,
					(space) => space.container?.key,
					cachedFetchSpaceApprovalsConfigs,
				);

				if (filteredSpaces.length === 0) {
					return runFallback();
				}

				return filteredSpaces.map(({ container }: RecommendedSpaceResponse) => ({
					label: container.name,
					value: container.spaceId,
					key: container.key,
					...(container.homepage && {
						homepage: {
							label: container.homepage.name,
							value: container.homepage.id,
							type: convertContentType(container.homepage.type),
						},
					}),
					iconPath: container.icon.path,
					type: container.type,
					typeSettings: container.typeSettings,
				}));
			}

			const response = await listEntities(searchText, ['space']);
			const contentTypeFiltered = response.filter((space) =>
				supportsContentType(space, filterContentType),
			);
			const filteredSpaces = await filterOutFirstPublishDisallowedSpaces(
				contentTypeFiltered,
				(space) => space.key,
				cachedFetchSpaceApprovalsConfigs,
			);
			return mapSearchResultsToOptions(filteredSpaces);
		},
		[
			listEntities,
			userId,
			fetchRecommendedSpaces,
			cachedFetchSpaceApprovalsConfigs,
			mapSearchResultsToOptions,
			shouldUseSmartRecommendedSpaces,
			recommendedSpaces,
			filterContentType,
			runFallback,
		],
	);

	return (
		<DebouncedSelector<ConfluenceSpaceOption>
			isLoading={Boolean(!recommendedSpaces && shouldUseSmartRecommendedSpaces)}
			loadOptions={loadOptions}
			onInputChange={(e) => {
				setHasSearchText(Boolean(e));
			}}
			options={
				hasSearchText || !shouldUseSmartRecommendedSpaces || !recommendedSpaces
					? undefined
					: [
							{
								label: formatMessage(messages.suggestedSpacesHeading),
								options: recommendedSpaces
									.filter((space) =>
										supportsContentType(
											{ typeSettings: space.spaceDetails.spaceTypeSettings },
											filterContentType,
										),
									)
									.filter(
										(space) =>
											!space.spaceDetails.key ||
											!firstPublishDisallowedKeys.has(space.spaceDetails.key),
									)
									.map((space) => ({
										label: space.name,
										value: space.id,
										key: space.spaceDetails.key,
										...(space.spaceDetails.homepage && {
											homepage: {
												label: space.spaceDetails.homepage.title,
												value: space.spaceDetails.homepage.id,
												type: convertContentType(space.spaceDetails.homepage.type),
											},
										}),
										iconPath: space.spaceDetails.icon?.path,
										type: space.spaceDetails.type,
										typeSettings: space.spaceDetails.spaceTypeSettings,
									})),
							},
						]
			}
			label={formatMessage(messages.spaceSelectorLabel)}
			{...props}
		/>
	);
};
